import React, { useEffect, useState } from "react";
import { Flex, Text } from "@radix-ui/themes";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, redirect, useLocation, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { TbPlus } from "react-icons/tb";
import { useAppStore } from "../../../stores/RootStore";
import RoleRequire from "../../../helpers/auth/RoleRequire";
import { Button } from "../../../ui/Button";
import { Separator } from "../../../ui/Separator";
import { DataTable } from "../../../ui/DataTable/DataTable";
import { FShift, SiteById, UserCompact } from "../../../types/types";
import { DateTime } from "luxon";
import { Input } from "../../../ui/Input/Input";
import { useRefreshShifts } from "../../../hooks/useRefreshStateHooks/useRefreshShifts";
import { useFetch } from "../../../hooks/useFetch";
import { SitesTableData, UsersTableData } from "../../../types/formDataTypes";

export const Organizations = () => {
  // Получение данных и обновление их в сторе.
  const currentSite = useAppStore((store) => store.currentSite);
  const currentUserSites = useAppStore((store) => store.currentUser?.attributes.sites);
  const setCurrentSite = useAppStore((store) => store.setCurrentSite);
  const [loading, setLoading] = useState(true);
  const [{ response, isLoading, error }, doFetchItem] = useFetch(`/users/sites`, true);
  const [sites, setSites] = useState<SiteById[]>();

  useEffect(() => {
    setLoading(true);
    doFetchItem();
  }, []);

  useEffect(() => {
    if (!response) {
      return;
    }
    setSites(response.data as unknown as SiteById[]);
    setLoading(false);
  }, [response]);

  const location = useLocation();
  const [search, setSearch] = React.useState("");

  const [compact, setCompact] = React.useState(
    location.pathname === "/organizations" || location.pathname === "/organizations/" ? false : true
  );

  const navigate = useNavigate();

  React.useEffect(() => {
    setCompact(
      location.pathname === "/organizations" || location.pathname === "/organizations" ? false : true
    );
  }, [location]);

  let nodes = sites?.map((data) => {
    return {
      id: data.id,
      name: data.attributes.name,
      short_name: data.attributes.short_name,
      image: data.attributes.image,
      subdomain: data.attributes.subdomain,
      timezone: data.attributes.timezone,
      user_status: data.attributes.user_status,
    };
  });

  // Сортировка по умолчанию
  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const handleSearch = (event: any) => {
    setSearch(event.target.value);
  };

  // Фильтрация через поиск
  data = {
    nodes: data.nodes?.filter((item) => (search ? item.name?.toLowerCase().includes(search.toLowerCase()) : true)),
  };

  const changeOrganization = (id: string) => {
    const site = currentUserSites?.find((userSite) => String(userSite.id) == id)!;
    setCurrentSite(site);
    navigate("/");
    //window.location.reload();
  };

  const { t, i18n } = useTranslation();

  const columns: ColumnDef<SitesTableData>[] = [
    {
      id: "active",
      cell: ({ row }) =>
        row.original.id == String(currentSite?.id) ? (
          <Button variant="outline">{t("active")}</Button>
        ) : (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              changeOrganization(row.original.id);
            }}
          >
            {t("go_to")}
          </Button>
        ),
    },
    {
      header: t("organization_name"),
      cell: ({ row }) => row.original.name,
    },
    {
      header: t("domen"),
      cell: ({ row }) => row.original.subdomain,
    },
    {
      header: t("role"),
      cell: ({ row }) => row.original.user_status,
    },
  ];

  const compactColumns: ColumnDef<SitesTableData>[] = [
    {
      header: t("title_name"),
      cell: (props) => (
        <NavLink to={props.row.original.id}>
          {({ isActive, isPending, isTransitioning }) => (
            <div className={`${isActive ? "text-primary" : ""}`}>
              <div className="font-medium truncate">{`${props.row.original.name}`}</div>
            </div>
          )}
        </NavLink>
      ),
    },
  ];

  return (
    <Flex width="100%" height="100%" className="bg-background">
      {compact ? (
        <Flex direction="column" style={{ maxWidth: 300 }}>
          <Flex
            className="flex-none h-12 w-full px-4"
            align="center"
            justify="between"
          >
            <Text
              className="flex-none text-base font-semibold cursor-pointer"
              onClick={() => navigate("/organizations")}
            >
              {t("my_organizations")}
            </Text>
            <Flex align="center">
              <RoleRequire role="maintainer">
                <Button
                  variant="default"
                  size="small_icon"
                  onClick={() => navigate("new")}
                >
                  <TbPlus />
                </Button>
              </RoleRequire>
            </Flex>
          </Flex>
          <Separator />
          <Flex className="h-12 flex-none px-4 w-full" align="center">
            <Input
              className="h-8"
              style={{ width: 220 }}
              placeholder={t("search")}
              onChange={handleSearch}
              value={search}
              autoComplete="off"
            />
          </Flex>
          <Separator />
          <DataTable
            data={data.nodes}
            columns={compactColumns}
            compact={compact}
          />
        </Flex>
      ) : (
        <Flex direction="column" width="100%">
          <Flex className="flex-none h-12 w-full px-4" align="center">
            <Text className="flex-none text-base font-semibold w-[300px]">
              {t("my_organizations")}
            </Text>
            <Separator orientation="vertical" />
            <Flex justify="start" px="4">
              <Input
                className="h-6"
                style={{ width: 220 }}
                placeholder={t("search")}
                onChange={handleSearch}
                value={search}
                autoComplete="off"
              />
            </Flex>
            <Flex width="100%" justify="end" gap="2">
              <RoleRequire role="maintainer">
                <NavLink to="new">
                  <Button
                    variant="default"
                    size="xs"
                    style={{ float: "right" }}
                  >
                    {t("create_organization")}
                  </Button>
                </NavLink>
              </RoleRequire>
            </Flex>
          </Flex>
          <Separator />
          <DataTable
            data={data.nodes}
            columns={columns}
            onClickRow={navigate}
          />
        </Flex>
      )}
      {compact ? <Separator orientation="vertical" /> : <></>}
      {compact ? <Outlet context={[sites, setSites]} /> : <></>}
    </Flex>
  );
};
